import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle,
  Eye,
  GraduationCap,
  Link as LinkIcon,
  Mail,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import {
  getUsecureDashboard,
  getUsecureFollowups,
  getUsecureClientFollowupReport,
  prepareUsecureMonthlyOperations,
  generateUsecureFollowups,
  saveUsecureCampaignEmailAsset,
  searchUsecureProjectCandidates,
  confirmUsecureFollowupProject,
  createUsecureFollowupProjectTask,
  refreshUsecureProjectTaskLinks,
  syncUsecureDashboard,
  updateUsecureFollowup,
  type UsecureCampaign,
  type UsecureClientFollowup,
  type UsecureClientReportResponse,
  type UsecureDashboard,
  type UsecureProjectLink,
} from '../services/usecureService';

interface UsecurePhishingCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EmailAssetDraft {
  fromName: string;
  fromAddress: string;
  subject: string;
  linksText: string;
  previewHtml: string;
  previewImageUrl: string;
}

const emptyDraft: EmailAssetDraft = {
  fromName: '',
  fromAddress: '',
  subject: '',
  linksText: '',
  previewHtml: '',
  previewImageUrl: '',
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: '2-digit' });
}

function pct(value?: number) {
  return `${Number(value || 0).toLocaleString('fr-CA', { maximumFractionDigits: 1 })} %`;
}

function number(value?: number) {
  return Number(value || 0).toLocaleString('fr-CA');
}

function formatUserName(user: { name?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null }) {
  const fullName = [user.firstName, user.lastName].map((part) => String(part || '').trim()).filter(Boolean).join(' ');
  return String(user.name || fullName || user.email || 'Utilisateur sans nom').trim();
}

function getUsecureBillingMonths(client: UsecureDashboard['clients'][number]) {
  const rows = new Map<string, { month: string; maxLearners: number }>();
  for (const item of client.dailyUsage || []) {
    const month = String(item.date || '').slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month)) continue;
    const current = rows.get(month)?.maxLearners || 0;
    rows.set(month, { month, maxLearners: Math.max(current, Number(item.maxLearners || 0)) });
  }
  for (const item of client.usage || []) {
    const month = String(item.startDate || '').slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month)) continue;
    const current = rows.get(month)?.maxLearners || 0;
    rows.set(month, { month, maxLearners: Math.max(current, Number(item.maxLearners || 0)) });
  }
  if (rows.size === 0) {
    rows.set('courant', { month: 'courant', maxLearners: Number(client.plan?.learnerCount || client.learnerSummary?.activeCount || 0) });
  }
  return Array.from(rows.values()).sort((a, b) => b.month.localeCompare(a.month, 'fr-CA'));
}

function getUsecureBillingUsers(client: UsecureDashboard['clients'][number]) {
  return (client.learners || [])
    .filter((learner) => learner.inactive !== true)
    .map((learner) => ({
      id: String(learner.id || learner.learnerId || learner.email || formatUserName(learner)),
      name: formatUserName(learner),
      email: String(learner.email || '').trim() || 'Courriel non disponible',
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr-CA'));
}

function getUsecureBillingServiceLabels(client: UsecureDashboard['clients'][number]) {
  const canonicalLabels: Record<string, string> = {
    uLearn: 'uLearn',
    uPhish: 'uPhish',
    uBreachEnabled: 'uBreach',
    uBreachProEnabled: 'uBreach Pro',
  };
  return (client.services || []).map((service) => canonicalLabels[service.key] || service.label).filter(Boolean);
}

function statusLabel(status?: string | null) {
  const raw = String(status || '').trim();
  if (!raw) return 'En cours';
  const lower = raw.toLowerCase();
  if (['active', 'running', 'in_progress', 'sending'].includes(lower)) return 'En cours';
  if (['scheduled', 'queued', 'pending'].includes(lower)) return 'Planifiée';
  return raw;
}

function campaignRiskTone(campaign: UsecureCampaign) {
  const compromiseRate = campaign.metrics?.compromiseRate || 0;
  const visitRate = campaign.metrics?.visitRate || 0;
  if (compromiseRate >= 5 || visitRate >= 15) return 'text-red-300 bg-red-500/15 border-red-400/30';
  if (compromiseRate > 0 || visitRate >= 5) return 'text-amber-200 bg-amber-500/15 border-amber-400/30';
  return 'text-emerald-200 bg-emerald-500/15 border-emerald-400/30';
}

function campaignToDraft(campaign: UsecureCampaign | null): EmailAssetDraft {
  if (!campaign) return emptyDraft;
  return {
    fromName: campaign.email?.fromName || '',
    fromAddress: campaign.email?.fromAddress || '',
    subject: campaign.email?.subject || '',
    linksText: (campaign.email?.links || []).join('\n'),
    previewHtml: campaign.email?.previewHtml || '',
    previewImageUrl: campaign.email?.previewImageUrl || '',
  };
}

function getEmailVariantLabel(key: string, label?: string) {
  if (key === 'short') return 'Suivi court';
  if (key === 'long') return 'Suivi long';
  if (key === 'upsell') return 'Option upsell';
  return label || key;
}

const followupStatusOptions: Array<{ value: UsecureClientFollowup['status']; label: string }> = [
  { value: 'draft', label: 'À préparer' },
  { value: 'ready', label: 'Prêt à envoyer' },
  { value: 'sent', label: 'Envoyé' },
  { value: 'responded', label: 'Réponse reçue' },
  { value: 'action_created', label: 'Action créée' },
  { value: 'closed', label: 'Fermé' },
];

function getFollowupStatusLabel(status?: string | null) {
  return followupStatusOptions.find((option) => option.value === status)?.label || 'À préparer';
}

function getFollowupEmailVariants(followup: UsecureClientFollowup) {
  const variants = followup.emailVariants || followup.generatedSummary.emailVariants;
  if (variants && Object.keys(variants).length > 0) {
    return Object.fromEntries(Object.entries(variants).map(([key, variant]) => [key, { ...variant, label: getEmailVariantLabel(key, variant.label) }]));
  }
  return {
    short: {
      label: 'Suivi court',
      subject: `Question rapide — suivi uSecure ${followup.clientName}`,
      body: `Bonjour,\n\nPetit suivi uSecure pour ${followup.clientName}. Avez-vous des questions sur les résultats du mois?\n\nMerci,\nL’équipe BZ`,
    },
    long: {
      label: 'Suivi long',
      subject: followup.generatedEmailSubject || `Résumé uSecure — ${followup.clientName}`,
      body: followup.generatedEmailBody || 'Résumé uSecure non disponible pour ce suivi.',
    },
    upsell: {
      label: 'Option upsell',
      subject: `Fonctionnalités uSecure — ${followup.clientName}`,
      body: `Bonjour,\n\nJe voulais valider si vous seriez intéressés à discuter des fonctionnalités uSecure auxquelles vous ne semblez pas avoir accès actuellement.\n\nL’objectif serait de voir si certaines options pourraient bonifier votre programme de sensibilisation et vos suivis mensuels.\n\nMerci,\nL’équipe BZ`,
    },
  };
}

function normalizeUsecureLifecycleValue(value?: string | null) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function isInactiveUsecureClient(client: { status?: string | null; accountType?: string | null }) {
  return [client.status, client.accountType]
    .some((value) => ['suspended', 'inactive', 'trialexpired', 'expired', 'cancelled', 'canceled', 'disabled', 'archived'].includes(normalizeUsecureLifecycleValue(value)));
}

function formatPortfolioAssignmentLine(servoComparison?: UsecureClientFollowup['servoComparison']) {
  const owner = String(servoComparison?.referentCyber || servoComparison?.prime || '').trim();
  return owner ? `Portefeuille : ${owner}` : 'Portefeuille : Non assigné';
}

export default function UsecurePhishingCampaignsModal({ isOpen, onClose }: UsecurePhishingCampaignsModalProps) {
  const [dashboard, setDashboard] = useState<UsecureDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<UsecureCampaign | null>(null);
  const [emailDetailOpen, setEmailDetailOpen] = useState(false);
  const [draft, setDraft] = useState<EmailAssetDraft>(emptyDraft);
  const [savingAsset, setSavingAsset] = useState(false);
  const [assetMessage, setAssetMessage] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'dashboard' | 'followups' | 'billing'>('dashboard');
  const [followups, setFollowups] = useState<UsecureClientFollowup[]>([]);
  const [periodMonth, setPeriodMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [followupMessage, setFollowupMessage] = useState<string | null>(null);
  const [reportsByFollowupId, setReportsByFollowupId] = useState<Record<number, UsecureClientReportResponse>>({});
  const [referentCyberFilter, setReferentCyberFilter] = useState('all');
  const [taskViewFilter, setTaskViewFilter] = useState<'all' | 'missing_task' | 'task_created' | 'needs_validation' | 'missing_project' | 'sent' | 'not_sent'>('all');
  const [selectedEmailVariantByFollowupId, setSelectedEmailVariantByFollowupId] = useState<Record<number, string>>({});
  const [projectSearchByFollowupId, setProjectSearchByFollowupId] = useState<Record<number, string>>({});
  const [projectCandidatesByFollowupId, setProjectCandidatesByFollowupId] = useState<Record<number, UsecureProjectLink[]>>({});
  const [highlightedFollowupCompanyId, setHighlightedFollowupCompanyId] = useState<string | null>(null);

  async function loadDashboard(mode: 'live' | 'cache' = 'live') {
    setLoading(true);
    setError(null);
    try {
      const data = await getUsecureDashboard(mode);
      setDashboard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement uSecure');
    } finally {
      setLoading(false);
    }
  }

  async function loadFollowups(month = periodMonth) {
    setLoadingFollowups(true);
    setFollowupMessage(null);
    try {
      const data = await getUsecureFollowups(month);
      setPeriodMonth(data.periodMonth);
      setFollowups(data.followups || []);
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors du chargement des suivis mensuels.');
    } finally {
      setLoadingFollowups(false);
    }
  }

  async function handleRefreshProjectTaskLinks() {
    setLoadingFollowups(true);
    setFollowupMessage(null);
    try {
      const data = await refreshUsecureProjectTaskLinks(periodMonth);
      setPeriodMonth(data.periodMonth);
      setFollowups(data.followups || []);
      setFollowupMessage('Statuts des tâches projet synchronisés.');
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la synchronisation des tâches projet.');
    } finally {
      setLoadingFollowups(false);
    }
  }

  async function handleGenerateFollowups(usecureCompanyId?: string) {
    setLoadingFollowups(true);
    setFollowupMessage(null);
    try {
      const data = await generateUsecureFollowups(periodMonth, usecureCompanyId);
      setFollowups((current) => {
        const byId = new Map(current.map((item) => [item.id, item]));
        for (const item of data.followups || []) byId.set(item.id, item);
        return Array.from(byId.values()).sort((a, b) => a.clientName.localeCompare(b.clientName));
      });
      setFollowupMessage(usecureCompanyId ? 'Suivi client généré.' : 'Suivis mensuels générés pour les clients synchronisés.');
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la génération des suivis.');
    } finally {
      setLoadingFollowups(false);
    }
  }

  async function handlePrepareMonthlyOperations() {
    setLoadingFollowups(true);
    setFollowupMessage(null);
    try {
      const data = await prepareUsecureMonthlyOperations(periodMonth);
      setPeriodMonth(data.periodMonth);
      setFollowups(data.followups || []);
      setActiveView('followups');
      setFollowupMessage(`Mois préparé : ${data.generatedCount} suivi(s), ${data.createdTaskCount} tâche(s) créée(s), ${data.existingTaskCount} déjà reliée(s), ${data.needsValidationCount} projet(s) à valider, ${data.missingProjectCount} sans projet fiable.`);
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la préparation mensuelle uSecure.');
    } finally {
      setLoadingFollowups(false);
    }
  }

  async function handleUpdateFollowup(id: number, payload: Parameters<typeof updateUsecureFollowup>[1]) {
    setFollowupMessage(null);
    try {
      const updated = await updateUsecureFollowup(id, payload);
      setFollowups((current) => current.map((item) => item.id === id ? updated : item));
      setFollowupMessage('Suivi mensuel mis à jour.');
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la mise à jour du suivi.');
    }
  }

  async function handleConfirmProject(followup: UsecureClientFollowup, projectLink?: UsecureProjectLink | null) {
    const link = projectLink || followup.servoComparison?.projectLink;
    if (!link) return;
    setFollowupMessage(null);
    try {
      const updated = await confirmUsecureFollowupProject(followup.id, link);
      setFollowups((current) => current.map((item) => item.id === followup.id ? updated : item));
      setProjectCandidatesByFollowupId((current) => ({ ...current, [followup.id]: [] }));
      setFollowupMessage('Projet lié confirmé manuellement.');
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la confirmation du projet.');
    }
  }

  async function handleSearchProjectCandidates(followup: UsecureClientFollowup) {
    const query = (projectSearchByFollowupId[followup.id] || followup.clientName).trim();
    if (!query) return;
    setFollowupMessage(null);
    try {
      const projects = await searchUsecureProjectCandidates(query);
      setProjectCandidatesByFollowupId((current) => ({ ...current, [followup.id]: projects }));
      setFollowupMessage(projects.length ? 'Projets candidats chargés.' : 'Aucun projet candidat trouvé.');
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la recherche de projets.');
    }
  }

  async function handleCreateProjectTask(followup: UsecureClientFollowup) {
    setFollowupMessage(null);
    try {
      const result = await createUsecureFollowupProjectTask(followup.id);
      setFollowups((current) => current.map((item) => item.id === followup.id ? { ...item, ...result.followup, projectTaskLink: result.projectTaskLink } : item));
      setFollowupMessage(result.created ? 'Tâche projet créée.' : 'Tâche projet déjà existante reliée au suivi.');
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la création de la tâche projet.');
    }
  }

  async function handleCreateMissingProjectTasks() {
    const candidates = followups.filter((followup: UsecureClientFollowup) => {
      if (referentCyberFilter !== 'all') {
        const owner = followup.servoComparison?.referentCyber || followup.servoComparison?.prime || 'Non assigné';
        if (owner !== referentCyberFilter) return false;
      }
      const linkStatus = followup.servoComparison?.projectLink?.status;
      return !followup.projectTaskLink?.taskId && (linkStatus === 'linked' || linkStatus === 'confirmed_manual');
    });
    if (!candidates.length) {
      setFollowupMessage('Aucune tâche manquante à créer pour les suivis filtrés.');
      return;
    }
    let created = 0;
    let existing = 0;
    for (const followup of candidates) {
      try {
        const result = await createUsecureFollowupProjectTask(followup.id);
        if (result.created) created += 1;
        else existing += 1;
        setFollowups((current) => current.map((item) => item.id === followup.id ? { ...item, ...result.followup, projectTaskLink: result.projectTaskLink } : item));
      } catch (err) {
        setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la création des tâches projet.');
        return;
      }
    }
    setFollowupMessage(`Tâches projet traitées : ${created} créée(s), ${existing} déjà existante(s).`);
  }

  async function handleOpenClientReport(followupId: number) {
    setFollowupMessage(null);
    try {
      const report = await getUsecureClientFollowupReport(followupId);
      setReportsByFollowupId((current) => ({ ...current, [followupId]: report }));
      const reportWindow = window.open('', '_blank');
      if (reportWindow) {
        reportWindow.document.open();
        reportWindow.document.write(report.report.printHtml);
        reportWindow.document.close();
        reportWindow.focus();
      } else {
        setFollowupMessage('Rapport généré. Le navigateur a bloqué l’ouverture automatique; autorise les fenêtres contextuelles pour l’imprimer en PDF.');
      }
    } catch (err) {
      setFollowupMessage(err instanceof Error ? err.message : 'Erreur lors de la génération du rapport client.');
    }
  }

  async function handleUseEmailVariant(followupId: number, variantKey: string) {
    setSelectedEmailVariantByFollowupId((current) => ({ ...current, [followupId]: variantKey }));
    setFollowupMessage('Version de courriel sélectionnée.');
  }

  async function handleMarkEmailVariantSent(followup: UsecureClientFollowup, variantKey: string, variant: { label?: string; subject?: string; body?: string }) {
    await handleUpdateFollowup(followup.id, {
      status: 'sent',
      sentEmailVariant: {
        key: variantKey,
        label: getEmailVariantLabel(variantKey, variant.label),
        subject: variant.subject || '',
        body: variant.body || '',
      },
    });
  }

  async function handleCopyEmailVariant(body: string) {
    try {
      await navigator.clipboard.writeText(body);
      setFollowupMessage('Courriel copié.');
    } catch {
      setFollowupMessage('Impossible de copier automatiquement; sélectionne le texte du courriel.');
    }
  }

  useEffect(() => {
    if (isOpen) {
      void loadDashboard('cache');
      void loadFollowups(periodMonth);
    }
    if (!isOpen) {
      setDashboard(null);
      setFollowups([]);
      setSelectedCampaign(null);
      setDraft(emptyDraft);
      setError(null);
      setAssetMessage(null);
      setFollowupMessage(null);
      setReportsByFollowupId({});
      setActiveView('dashboard');
      setEmailDetailOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeActionMenus = (event: MouseEvent | TouchEvent | KeyboardEvent) => {
      const isEscape = event instanceof KeyboardEvent && event.key === 'Escape';
      if (event instanceof KeyboardEvent && !isEscape) return;
      const target = event.target as Node | null;
      document.querySelectorAll<HTMLDetailsElement>('details[data-usecure-click-outside-menu][open]').forEach((menu) => {
        if (!isEscape && target && menu.contains(target)) return;
        menu.removeAttribute('open');
      });
    };
    document.addEventListener('mousedown', closeActionMenus);
    document.addEventListener('touchstart', closeActionMenus);
    document.addEventListener('keydown', closeActionMenus);
    return () => {
      document.removeEventListener('mousedown', closeActionMenus);
      document.removeEventListener('touchstart', closeActionMenus);
      document.removeEventListener('keydown', closeActionMenus);
    };
  }, [isOpen]);

  const dashboardClients = useMemo(() => {
    return (dashboard?.clients || []).filter((client) => !isInactiveUsecureClient(client));
  }, [dashboard]);

  const filteredClients = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return dashboardClients;
    return dashboardClients
      .map((client) => ({
        ...client,
        activeCampaigns: client.activeCampaigns.filter(
          (campaign) =>
            client.name.toLowerCase().includes(query) ||
            campaign.name.toLowerCase().includes(query) ||
            String(campaign.status || '').toLowerCase().includes(query)
        ),
      }))
      .filter((client) => client.name.toLowerCase().includes(query) || client.activeCampaigns.length > 0);
  }, [dashboardClients, search]);

  const billingClients = useMemo(() => filteredClients.map((client) => {
    const months = getUsecureBillingMonths(client);
    const users = getUsecureBillingUsers(client);
    const peakMonth = months.reduce((peak, month) => month.maxLearners > peak.maxLearners ? month : peak, months[0] || { month: 'courant', maxLearners: 0 });
    return { client, months, users, peakMonth };
  }), [filteredClients]);

  const billingSummary = useMemo(() => billingClients.reduce((summary, row) => {
    summary.clients += 1;
    summary.currentUsers += row.users.length;
    summary.peakUsers += row.peakMonth.maxLearners || 0;
    return summary;
  }, { clients: 0, currentUsers: 0, peakUsers: 0 }), [billingClients]);

  const referentCyberOptions = useMemo(() => {
    return Array.from(new Set(
      followups
        .map((followup) => followup.servoComparison?.referentCyber || 'Non assigné')
        .filter(Boolean)
    )).sort((a, b) => a.localeCompare(b, 'fr-CA'));
  }, [followups]);

  const displayedFollowups = useMemo(() => {
    if (referentCyberFilter === 'all') return followups;
    return followups.filter((followup) => (followup.servoComparison?.referentCyber || 'Non assigné') === referentCyberFilter);
  }, [followups, referentCyberFilter]);

  const priorityFollowups = useMemo(() => {
    return [...followups]
      .map((followup) => {
        const recommendations = followup.generatedSummary?.recommendations || [];
        const incompleteUsers = followup.generatedSummary?.incompleteTrainingUsers || [];
        const openBreaches = Number(followup.health?.inputs?.openBreaches || 0);
        const compromised = Number(followup.health?.inputs?.compromises || 0);
        const completion = Number(followup.health?.inputs?.trainingCompletionRate || 0);
        const reasons = [
          incompleteUsers.length ? `${incompleteUsers.length} utilisateur(s) avec formation incomplète` : '',
          openBreaches ? `${openBreaches} exposition(s) uBreach ouverte(s)` : '',
          compromised ? `${compromised} compromission(s) phishing` : '',
          completion > 0 && completion < 85 ? `Formation à ${pct(completion)}` : '',
          followup.status === 'draft' ? 'Suivi à préparer' : '',
        ].filter(Boolean);
        const score = (100 - Number(followup.health?.score || 0)) + (incompleteUsers.length * 8) + (openBreaches * 10) + (compromised * 12) + (followup.status === 'draft' ? 8 : 0);
        return { followup, score, reasons: reasons.length ? reasons : recommendations.slice(0, 2) };
      })
      .filter((item) => item.score >= 25 || item.reasons.length > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [followups]);

  const advisorWorkload = useMemo(() => {
    const rows = new Map<string, { name: string; total: number; pending: number; sent: number; responded: number; highRisk: number }>();
    for (const followup of followups) {
      const name = followup.servoComparison?.referentCyber || 'Non assigné';
      const row = rows.get(name) || { name, total: 0, pending: 0, sent: 0, responded: 0, highRisk: 0 };
      row.total += 1;
      if (['draft', 'ready', 'action_created'].includes(followup.status)) row.pending += 1;
      if (followup.status === 'sent') row.sent += 1;
      if (['responded', 'closed'].includes(followup.status)) row.responded += 1;
      if (Number(followup.health?.score || 0) < 70) row.highRisk += 1;
      rows.set(name, row);
    }
    return Array.from(rows.values()).sort((a, b) => b.pending - a.pending || a.name.localeCompare(b.name, 'fr-CA'));
  }, [followups]);

  const projectLinkSummary = useMemo(() => {
    return followups.reduce((summary, followup) => {
      const status = followup.servoComparison?.projectLink?.status;
      if (status === 'linked' || status === 'confirmed_manual') summary.linked += 1;
      else if (status === 'needs_validation') summary.needsValidation += 1;
      else summary.missing += 1;
      return summary;
    }, { linked: 0, needsValidation: 0, missing: 0 });
  }, [followups]);

  const usecureTaskSummary = useMemo(() => {
    return followups.reduce((summary, followup) => {
      const linkStatus = followup.servoComparison?.projectLink?.status;
      const hasTask = Boolean(followup.projectTaskLink?.taskId);
      summary.total += 1;
      if (hasTask) summary.taskCreated += 1;
      if (!hasTask && (linkStatus === 'linked' || linkStatus === 'confirmed_manual')) summary.missingTask += 1;
      if (linkStatus === 'needs_validation') summary.needsValidation += 1;
      if (!linkStatus) summary.missingProject += 1;
      if (followup.sentAt || followup.sentEmailVariant) summary.sent += 1;
      return summary;
    }, { total: 0, taskCreated: 0, missingTask: 0, needsValidation: 0, missingProject: 0, sent: 0 });
  }, [followups]);

  const taskSummaryRows = useMemo(() => {
    return displayedFollowups.filter((followup) => {
      const linkStatus = followup.servoComparison?.projectLink?.status;
      const hasTask = Boolean(followup.projectTaskLink?.taskId);
      if (taskViewFilter === 'missing_task') return !hasTask && (linkStatus === 'linked' || linkStatus === 'confirmed_manual');
      if (taskViewFilter === 'task_created') return hasTask;
      if (taskViewFilter === 'needs_validation') return linkStatus === 'needs_validation';
      if (taskViewFilter === 'missing_project') return !linkStatus;
      if (taskViewFilter === 'sent') return Boolean(followup.sentAt || followup.sentEmailVariant);
      if (taskViewFilter === 'not_sent') return !followup.sentAt && !followup.sentEmailVariant;
      return true;
    });
  }, [displayedFollowups, taskViewFilter]);

  async function handleSync() {
    setSyncing(true);
    setError(null);
    try {
      const data = await syncUsecureDashboard();
      setDashboard(data);
      setSelectedCampaign(null);
      setEmailDetailOpen(false);
      setDraft(emptyDraft);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la synchronisation uSecure');
    } finally {
      setSyncing(false);
    }
  }

  function selectCampaign(campaign: UsecureCampaign) {
    setSelectedCampaign(campaign);
    setDraft(campaignToDraft(campaign));
    setEmailDetailOpen(true);
    setAssetMessage(null);
  }

  function openClientFollowup(usecureCompanyId: string) {
    setActiveView('followups');
    setReferentCyberFilter('all');
    setHighlightedFollowupCompanyId(usecureCompanyId);
    window.setTimeout(() => {
      const selector = `[data-usecure-followup-company-id="${CSS.escape(usecureCompanyId)}"]`;
      document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  }

  async function handleSaveAsset() {
    if (!selectedCampaign) return;
    setSavingAsset(true);
    setAssetMessage(null);
    try {
      await saveUsecureCampaignEmailAsset(selectedCampaign.id, {
        fromName: draft.fromName.trim() || null,
        fromAddress: draft.fromAddress.trim() || null,
        subject: draft.subject.trim() || null,
        links: draft.linksText.split('\n').map((item) => item.trim()).filter(Boolean),
        previewHtml: draft.previewHtml.trim() || null,
        previewImageUrl: draft.previewImageUrl.trim() || null,
      });
      setAssetMessage('Détails du courriel sauvegardés dans PRIsME.');
      await loadDashboard('cache');
    } catch (err) {
      setAssetMessage(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingAsset(false);
    }
  }

  if (!isOpen) return null;

  const summary = dashboard?.summary;
  const totalLearners = dashboardClients.reduce((sum, client) => sum + (client.learnerSummary?.activeCount || 0), 0);
  const trainingCompletion = (() => {
    const assigned = dashboardClients.reduce((sum, client) => sum + (client.courseSummary?.assignedCount || 0), 0);
    const completed = dashboardClients.reduce((sum, client) => sum + (client.courseSummary?.completedCount || 0), 0);
    return assigned ? Math.round((completed / assigned) * 1000) / 10 : 0;
  })();
  const highRiskLearners = dashboardClients.reduce((sum, client) => sum + (client.learnerSummary?.highRiskCount || 0), 0);
  const openBreaches = dashboardClients.reduce((sum, client) => sum + (client.breachSummary?.openCount || 0), 0);
  const needsManualEmailAssets = dashboard?.schemaSummary?.hasEmailAssetFields === false;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-2 backdrop-blur-sm lg:p-3">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="my-3 flex max-h-[96vh] w-full max-w-[min(1680px,96vw)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-2xl shadow-slate-300/40"
          >
            <div className="border-b border-slate-200 bg-white px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-blue-50 p-2 text-blue-700 ring-1 ring-blue-100">
                    <ShieldCheck className="h-7 w-7" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Automatisation des suivis uSecure</h2>
                    <p className="mt-0.5 text-xs text-slate-600">
                      Vue PRIsME des clients uSecure : formations, hameçonnage, utilisation, expositions uBreach et brouillon de suivi mensuel.
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {dashboard?.lastSyncAt
                        ? `Données affichées depuis la dernière synchronisation : ${new Date(dashboard.lastSyncAt).toLocaleString('fr-CA')}`
                        : 'PRIsME affiche d’abord la plus récente synchronisation sauvegardée; utilise Actualiser les données pour rafraîchir.'}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative max-w-md flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Rechercher un client ou une campagne…"
                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    <span className="font-semibold text-slate-900">Dernière synchro :</span>{' '}
                    {dashboard?.lastSyncAt ? new Date(dashboard.lastSyncAt).toLocaleString('fr-CA') : 'aucune donnée sauvegardée'}
                  </div>
                  <button
                    type="button"
                    onClick={handleSync}
                    disabled={loading || syncing}
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-lg shadow-blue-500/20 hover:bg-blue-500 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                    Actualiser les données
                  </button>
                  <details data-usecure-click-outside-menu className="relative">
                    <summary className="list-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">⋯</summary>
                    <div className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-2 text-xs shadow-xl">
                      <button type="button" onClick={() => void loadDashboard('cache')} disabled={loading || syncing} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Afficher dernière synchronisation</button>
                      <button type="button" onClick={handleSync} disabled={loading || syncing} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Forcer une synchronisation uSecure</button>
                    </div>
                  </details>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveView('dashboard')}
                    className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${activeView === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  >
                    Tableau de bord
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveView('followups')}
                    className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${activeView === 'followups' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  >
                    Suivis mensuels
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveView('billing')}
                    className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${activeView === 'billing' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  >
                    Facturation
                  </button>
                </div>
                {activeView === 'followups' && (
                  <div data-usecure-followups-toolbar className="flex flex-wrap items-center justify-end gap-2">
                    <input aria-label="Mois des suivis uSecure" type="month" value={periodMonth} onChange={(event) => setPeriodMonth(event.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-900 outline-none focus:border-blue-500" />
                    <select aria-label="Filtrer par propriétaire de portefeuille" value={referentCyberFilter} onChange={(event) => setReferentCyberFilter(event.target.value)} className="h-8 min-w-[190px] rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500">
                      <option value="all">Tous les portefeuilles</option>
                      {referentCyberOptions.map((referent) => <option key={referent} value={referent}>{referent}</option>)}
                    </select>
                    <button type="button" onClick={() => void handlePrepareMonthlyOperations()} disabled={loadingFollowups} className="h-8 rounded-lg bg-indigo-600 px-4 text-xs font-semibold text-white shadow-sm shadow-indigo-500/20 hover:bg-indigo-500 disabled:opacity-50">Préparer le mois</button>
                    <details data-usecure-advanced-actions data-usecure-click-outside-menu className="relative">
                      <summary className="list-none h-8 cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Actions avancées ▾</summary>
                      <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-2 text-xs shadow-xl">
                        <button type="button" onClick={() => void loadFollowups(periodMonth)} disabled={loadingFollowups} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Charger seulement</button>
                        <button type="button" onClick={() => void handleGenerateFollowups()} disabled={loadingFollowups} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Générer seulement les suivis</button>
                        <button type="button" onClick={() => void handleRefreshProjectTaskLinks()} disabled={loadingFollowups} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Mettre à jour les tâches</button>
                        <button type="button" onClick={() => void handleCreateMissingProjectTasks()} disabled={loadingFollowups} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Créer tâches manquantes</button>
                        <span className="sr-only">Créer toutes les tâches manquantes</span>
                      </div>
                    </details>
                  </div>
                )}
              </div>
            </div>

            <div data-usecure-scroll-container className="flex-1 overflow-y-auto bg-slate-50 p-6">
              {error && (
                <div className="mb-5 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-amber-100">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Connexion uSecure à vérifier</p>
                      <p className="mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {dashboard?.liveError && (
                <div className="mb-5 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-amber-100">
                  Données affichées depuis le cache PRIsME. Erreur live : {dashboard.liveError}
                </div>
              )}

              {needsManualEmailAssets && (
                <div className="mb-5 rounded-2xl border border-blue-400/30 bg-blue-500/10 p-4 text-sm text-blue-100">
                  La documentation GraphQL uSecure ne semble pas exposer directement l’adresse d’envoi, les liens et le visuel du courriel.
                  PRIsME conserve donc des champs complémentaires par campagne, modifiables dans le panneau de droite.
                </div>
              )}

              {activeView === 'billing' && (
                <div data-usecure-billing-tab className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Clients facturables</p>
                      <p className="mt-2 text-3xl font-bold text-slate-900">{number(billingSummary.clients)}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Maximum mensuel total</p>
                      <p className="mt-2 text-3xl font-bold text-slate-900">{number(billingSummary.peakUsers)}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Utilisateurs listés</p>
                      <p className="mt-2 text-3xl font-bold text-slate-900">{number(billingSummary.currentUsers)}</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                    <p className="font-semibold">Facturation uSecure — vue de validation</p>
                    <p className="mt-1 text-blue-800">Chaque client est réduit par défaut. Développe un client pour voir le maximum d’utilisateurs par mois, puis la liste des utilisateurs inclus dans la facturation synchronisée.</p>
                  </div>

                  {loading && <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">Chargement des données de facturation uSecure…</div>}
                  {!loading && billingClients.length === 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">
                      Aucun client uSecure synchronisé pour la facturation.
                    </div>
                  )}

                  {!loading && billingClients.length > 0 && (
                    <div className="space-y-3">
                      {billingClients.map(({ client, months, users }) => {
                        const billingServices = getUsecureBillingServiceLabels(client);
                        return (
                        <details key={client.usecureCompanyId} data-usecure-billing-client className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                          <summary className="cursor-pointer list-none p-4">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                              <div className="min-w-0">
                                <h3 className="truncate text-lg font-bold text-slate-900">{client.name}</h3>
                                <p className="mt-0.5 text-xs text-slate-500">Plan : {client.plan?.name || 'Non précisé'} · ID uSecure : {client.usecureCompanyId}</p>
                              </div>
                              <div className="flex flex-wrap gap-2 text-xs font-semibold">
                                {billingServices.map((service) => (
                                  <span key={service} className="rounded-full bg-blue-50 px-3 py-1 text-blue-700">{service}</span>
                                ))}
                                {billingServices.length === 0 && <span className="rounded-full bg-amber-50 px-3 py-1 text-amber-700">Services à valider</span>}
                              </div>
                            </div>
                          </summary>
                          <div className="border-t border-slate-100 p-4">
                            <details data-usecure-billing-details className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                              <summary className="cursor-pointer text-sm font-bold text-slate-900">
                                Détails de facturation — maximums mensuels et utilisateurs inclus ({users.length})
                              </summary>
                              <div className="mt-3 grid gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                                <section data-usecure-billing-months className="rounded-xl border border-slate-200 bg-white p-3">
                                  <h4 className="text-sm font-bold text-slate-900">Maximum d’utilisateurs par mois ({months.length})</h4>
                                  <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
                                    <table className="w-full text-left text-xs">
                                      <thead className="bg-slate-50 text-slate-600"><tr><th className="px-3 py-2">Mois</th><th className="px-3 py-2 text-right">Maximum utilisateurs</th></tr></thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {months.map((month) => <tr key={month.month}><td className="px-3 py-2 font-semibold text-slate-900">{month.month}</td><td className="px-3 py-2 text-right text-slate-700">{number(month.maxLearners)}</td></tr>)}
                                      </tbody>
                                    </table>
                                  </div>
                                </section>

                                <section data-usecure-billing-users className="rounded-xl border border-slate-200 bg-white p-3">
                                  <h4 className="text-sm font-bold text-slate-900">Utilisateurs inclus dans la facturation ({users.length})</h4>
                                  <div className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                                    <table className="w-full text-left text-xs">
                                      <thead className="sticky top-0 bg-slate-50 text-slate-600"><tr><th className="px-3 py-2">Nom</th><th className="px-3 py-2">Adresse courriel</th></tr></thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {users.map((user) => <tr key={user.id}><td className="px-3 py-2 font-semibold text-slate-900">{user.name}</td><td className="px-3 py-2 text-slate-700">{user.email}</td></tr>)}
                                        {users.length === 0 && <tr><td colSpan={2} className="px-3 py-4 text-center text-slate-500">Aucun utilisateur actif synchronisé pour ce client.</td></tr>}
                                      </tbody>
                                    </table>
                                  </div>
                                </section>
                              </div>
                            </details>
                          </div>
                        </details>
                      );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeView === 'followups' && (
                <div className="space-y-5">
                  {followupMessage && <p className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">{followupMessage}</p>}

                  {!loadingFollowups && followups.length > 0 && (
                    <details className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                      <summary className="cursor-pointer text-sm font-bold text-slate-900">
                        Vue de pilotage — {priorityFollowups.length} priorité(s), {advisorWorkload.length} référent(s)
                      </summary>
                      <div className="mt-3 grid gap-3 lg:grid-cols-[1.2fr_0.8fr]">
                        <section className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs text-slate-700 lg:col-span-2">
                          <p className="font-semibold text-slate-900">Liens projets PRIsME — {periodMonth}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="rounded-full bg-white px-3 py-1 font-semibold text-blue-700">Projets liés : {projectLinkSummary.linked}</span>
                            <span className="rounded-full bg-white px-3 py-1 font-semibold text-amber-700">À valider : {projectLinkSummary.needsValidation}</span>
                            <span className="rounded-full bg-white px-3 py-1 font-semibold text-slate-700">Sans projet : {projectLinkSummary.missing}</span>
                          </div>
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700 lg:col-span-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="font-semibold text-slate-900">Tâches uSecure du mois</p>
                              <p className="mt-1 text-slate-500">{usecureTaskSummary.taskCreated} créée(s), {usecureTaskSummary.missingTask} manquante(s), {usecureTaskSummary.needsValidation} projet(s) à valider.</p>
                            </div>
                            <select value={taskViewFilter} onChange={(event) => setTaskViewFilter(event.target.value as typeof taskViewFilter)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700">
                              <option value="all">Tous</option>
                              <option value="missing_task">Sans tâche</option>
                              <option value="task_created">Tâche créée</option>
                              <option value="needs_validation">Projet à valider</option>
                              <option value="missing_project">Sans projet</option>
                              <option value="sent">Envoyé</option>
                              <option value="not_sent">À envoyer</option>
                            </select>
                          </div>
                          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-100">
                            <table className="w-full min-w-[780px] text-left text-xs">
                              <thead className="bg-slate-50 text-slate-600"><tr><th className="px-3 py-2">Client</th><th className="px-3 py-2">Projet</th><th className="px-3 py-2">Tâche</th><th className="px-3 py-2">Statut tâche</th><th className="px-3 py-2">Échéance</th><th className="px-3 py-2">Temps saisi</th><th className="px-3 py-2">Courriel</th></tr></thead>
                              <tbody className="divide-y divide-slate-100">
                                {taskSummaryRows.map((followup) => (
                                  <tr key={followup.id} className="align-top">
                                    <td className="px-3 py-2 font-semibold text-slate-900">{followup.clientName}</td>
                                    <td className="px-3 py-2 text-slate-700">{followup.servoComparison?.projectLink?.projectTitle || (followup.servoComparison?.projectLink?.status === 'needs_validation' ? 'À valider' : 'Sans projet')}</td>
                                    <td className="px-3 py-2 text-slate-700">{followup.projectTaskLink?.taskId ? <a href={followup.projectTaskLink.url} target="_blank" rel="noreferrer" className="font-semibold text-blue-700 hover:text-blue-900">{followup.projectTaskLink.title}</a> : 'Sans tâche'}</td>
                                    <td className="px-3 py-2 text-slate-700">{followup.projectTaskLink?.status || '—'}{typeof followup.projectTaskLink?.percent === 'number' ? ` · ${followup.projectTaskLink.percent} %` : ''}</td>
                                    <td className="px-3 py-2 text-slate-700">{formatDate(followup.projectTaskLink?.dueDate)}</td>
                                    <td className="px-3 py-2 text-slate-700">{Math.round(Number(followup.projectTaskLink?.timeSpentMinutes || 0) / 60 * 100) / 100} h</td>
                                    <td className="px-3 py-2 text-slate-700">{followup.sentEmailVariant?.label || (followup.sentAt ? 'Envoyé' : 'À envoyer')}</td>
                                  </tr>
                                ))}
                                {taskSummaryRows.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-500">Aucun suivi pour ce filtre.</td></tr>}
                              </tbody>
                            </table>
                          </div>
                        </section>

                        <section data-usecure-priority-clients>
                          <div className="flex flex-wrap gap-2">
                            {priorityFollowups.slice(0, 4).map(({ followup, reasons }, index) => (
                              <div key={followup.id} className="min-w-[220px] flex-1 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900">{index + 1}. {followup.clientName}</p>
                                    <p className="text-[11px] text-slate-500">{followup.servoComparison?.referentCyber || 'Non assigné'}</p>
                                  </div>
                                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{followup.health?.score ?? '—'} / 100</span>
                                </div>
                                <p className="mt-1 text-xs text-slate-700">{reasons.slice(0, 2).join(' · ') || 'Suivi à valider'}</p>
                              </div>
                            ))}
                            {priorityFollowups.length === 0 && <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">Aucun client prioritaire détecté pour ce mois.</p>}
                          </div>
                        </section>

                        <section data-usecure-advisor-workload className="overflow-hidden rounded-xl border border-slate-200">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-slate-50 text-slate-600"><tr><th className="px-3 py-2">Référent</th><th className="px-3 py-2 text-right">Clients</th><th className="px-3 py-2 text-right">À faire</th><th className="px-3 py-2 text-right">Risque</th></tr></thead>
                            <tbody className="divide-y divide-slate-100">
                              {advisorWorkload.map((row) => (
                                <tr key={row.name}><td className="px-3 py-2 font-semibold text-slate-900">{row.name}<button type="button" onClick={() => setReferentCyberFilter(row.name)} className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100">Voir seulement mes clients</button></td><td className="px-3 py-2 text-right text-slate-700">{row.total}</td><td className="px-3 py-2 text-right text-blue-700">{row.pending}</td><td className="px-3 py-2 text-right text-amber-700">{row.highRisk}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </section>
                      </div>
                    </details>
                  )}

                  {loadingFollowups && <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">Chargement des suivis mensuels…</div>}
                  {!loadingFollowups && followups.length === 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">
                      Aucun suivi mensuel pour {periodMonth}. Génère les suivis à partir de la dernière synchronisation uSecure.
                    </div>
                  )}
                  {!loadingFollowups && followups.length > 0 && displayedFollowups.length === 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">
                      Aucun suivi pour le référent Cyber sélectionné.
                    </div>
                  )}
                  {!loadingFollowups && displayedFollowups.length > 0 && (
                    <div className="grid gap-4">
                      {displayedFollowups.map((followup) => (
                        <div
                          key={followup.id}
                          data-usecure-followup-card
                          data-usecure-followup-company-id={followup.usecureCompanyId}
                          className={`rounded-2xl border bg-white p-5 shadow-sm ${highlightedFollowupCompanyId === followup.usecureCompanyId ? 'border-blue-300 ring-2 ring-blue-100' : 'border-slate-200'}`}
                        >
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="text-xl font-bold text-slate-900">{followup.clientName}</h4>
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{followup.periodMonth}</span>
                                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{getFollowupStatusLabel(followup.status)}</span>
                              </div>
                              <details data-usecure-project-link-manager className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                                <summary className="cursor-pointer list-none">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="font-semibold text-slate-900">Gestion liaison aux projets</p>
                                      <p className="mt-0.5 truncate text-xs text-slate-600">
                                        {followup.servoComparison?.projectLink ? (
                                          <>
                                            Projet lié : <span className="font-semibold text-slate-900">{followup.servoComparison.projectLink.projectTitle}</span>
                                            {followup.servoComparison.projectLink.matchScore ? ` · ${Math.round(followup.servoComparison.projectLink.matchScore * 100)} %` : ''}
                                          </>
                                        ) : 'Aucun projet PRIsME fiable trouvé'}
                                      </p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                      {followup.servoComparison?.projectLink && (
                                        <a href={followup.servoComparison.projectLink.url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="rounded-full bg-blue-50 px-2 py-0.5 font-semibold text-blue-700 hover:bg-blue-100 hover:text-blue-900">Ouvrir le projet</a>
                                      )}
                                      {followup.servoComparison?.projectLink?.status === 'needs_validation' && <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">À valider</span>}
                                      <span className="font-semibold text-blue-700">Modifier</span>
                                    </div>
                                  </div>
                                </summary>
                                <div className="mt-3 border-t border-slate-200 pt-3 text-xs text-slate-700">
                                  <p className="font-semibold text-slate-900">Pilotage projet — portefeuille client</p>
                                  <p className="mt-1"><span className="font-semibold">{formatPortfolioAssignmentLine(followup.servoComparison)}</span>{followup.servoComparison?.portfolioName ? ` · ${followup.servoComparison.portfolioName}` : ''}</p>
                                  {followup.servoComparison?.matchedClientName && (
                                    <p className="mt-1 text-slate-600">
                                      Client Pilotage projet trouvé : <span className="font-semibold text-slate-900">{followup.servoComparison.matchedClientName}</span>
                                      {followup.servoComparison.matchedAlias ? ` via alias ${followup.servoComparison.matchedAlias}` : ''}
                                      {followup.servoComparison.matchScore ? ` · concordance ${Math.round(followup.servoComparison.matchScore * 100)} %` : ''}
                                    </p>
                                  )}
                                  {followup.servoComparison?.error && <p className="mt-1 text-amber-700">{followup.servoComparison.error}</p>}
                                  {followup.servoComparison?.projectLink ? (
                                    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                                      <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                          <p className="font-semibold text-slate-900">Projet lié</p>
                                          <p className="mt-1"><span className="font-semibold">{followup.servoComparison.projectLink.projectTitle}</span>{followup.servoComparison.projectLink.matchScore ? ` · concordance ${Math.round(followup.servoComparison.projectLink.matchScore * 100)} %` : ''}</p>
                                        </div>
                                        {followup.servoComparison.projectLink.status === 'confirmed_manual' && <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">Confirmé manuellement</span>}
                                      </div>
                                      {followup.servoComparison.projectLink.status === 'needs_validation' && <p className="mt-1 font-semibold text-amber-700">À valider avant création de tâche</p>}
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        <a href={followup.servoComparison.projectLink.url} target="_blank" rel="noreferrer" className="inline-flex font-semibold text-blue-700 hover:text-blue-900">Ouvrir le projet</a>
                                        {followup.servoComparison.projectLink.status !== 'confirmed_manual' && <button type="button" onClick={() => void handleConfirmProject(followup)} className="rounded-lg bg-blue-600 px-2 py-1 font-semibold text-white hover:bg-blue-500">Confirmer ce projet</button>}
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="mt-3 rounded-lg border border-amber-200 bg-white p-3 font-semibold text-amber-700">Aucun projet PRIsME fiable trouvé pour ce client.</p>
                                  )}
                                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                                    <p className="font-semibold text-slate-900">Choisir un autre projet</p>
                                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                                      <input value={projectSearchByFollowupId[followup.id] ?? followup.clientName} onChange={(event) => setProjectSearchByFollowupId((current) => ({ ...current, [followup.id]: event.target.value }))} className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 outline-none focus:border-blue-500" />
                                      <button type="button" onClick={() => void handleSearchProjectCandidates(followup)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 font-semibold text-slate-700 hover:bg-slate-50">Rechercher</button>
                                    </div>
                                    {(projectCandidatesByFollowupId[followup.id] || []).length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        {(projectCandidatesByFollowupId[followup.id] || []).map((candidate) => (
                                          <button key={candidate.projectId} type="button" onClick={() => void handleConfirmProject(followup, candidate)} className="block w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-left hover:bg-blue-50">
                                            <span className="font-semibold text-slate-900">{candidate.projectTitle}</span>
                                            <span className="ml-2 text-slate-500">{candidate.ownerName || 'Non assigné'} · {Math.round((candidate.matchScore || 0) * 100)} %</span>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </details>
                              {followup.projectTaskLink?.taskId && (
                                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                                  <p className="font-semibold">Tâche projet</p>
                                  <p className="mt-1"><span className="font-semibold">{followup.projectTaskLink.title}</span>{followup.projectTaskLink.status ? ` · ${followup.projectTaskLink.status}` : ''}</p>
                                  {followup.projectTaskLink.dueDate && <p className="mt-1">Échéance : {formatDate(followup.projectTaskLink.dueDate)}</p>}
                                  <a href={followup.projectTaskLink.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex font-semibold text-emerald-700 hover:text-emerald-900">Ouvrir la tâche</a>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-stretch gap-2 sm:min-w-[260px] sm:items-end">
                              <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                                <span className="text-slate-500">Statut</span>
                                <select
                                  value={followup.status}
                                  onChange={(event) => void handleUpdateFollowup(followup.id, { status: event.target.value as UsecureClientFollowup['status'] })}
                                  className="min-w-[150px] border-0 bg-transparent text-xs font-bold text-slate-900 outline-none"
                                >
                                  {followupStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </select>
                              </label>
                              <div className="flex flex-wrap justify-end gap-2">
                                {!followup.projectTaskLink?.taskId && (followup.servoComparison?.projectLink?.status === 'linked' || followup.servoComparison?.projectLink?.status === 'confirmed_manual') && <button type="button" onClick={() => void handleCreateProjectTask(followup)} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500">Créer tâche projet</button>}
                                <button type="button" onClick={() => void handleOpenClientReport(followup.id)} className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200">Rapport client</button>
                                <details data-usecure-followup-more-actions data-usecure-click-outside-menu className="relative">
                                  <summary className="list-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">⋯</summary>
                                  <div className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-2 text-xs shadow-xl">
                                    <button type="button" onClick={() => void handleGenerateFollowups(followup.usecureCompanyId)} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50">Régénérer ce suivi</button>
                                    <button type="button" onClick={() => void handleOpenClientReport(followup.id)} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50">Rapport client / PDF</button>
                                    <button type="button" onClick={() => void handleUpdateFollowup(followup.id, { status: 'sent' })} className="block w-full rounded-lg px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50">Marquer envoyé</button>
                                  </div>
                                </details>
                              </div>
                            </div>
                          </div>

                          {reportsByFollowupId[followup.id] && (
                            <div data-usecure-client-report-tile className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4 text-slate-800">
                              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Historique mensuel / tendance</p>
                              <p className="mt-2 text-sm text-slate-700">{reportsByFollowupId[followup.id].trend.summary}</p>
                              <div className="mt-3 overflow-hidden rounded-lg border border-blue-100 bg-white">
                                <table className="w-full text-left text-xs text-slate-700">
                                  <thead className="bg-blue-50 text-slate-700"><tr><th className="px-3 py-2">Mois</th><th className="px-3 py-2">Santé</th><th className="px-3 py-2">Formation</th><th className="px-3 py-2">Compromis</th><th className="px-3 py-2">Expositions</th></tr></thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {reportsByFollowupId[followup.id].trend.points.map((point) => (
                                      <tr key={point.periodMonth}><td className="px-3 py-2">{point.periodMonth}</td><td className="px-3 py-2">{point.healthScore}</td><td className="px-3 py-2">{point.completionRate ?? '—'} %</td><td className="px-3 py-2">{point.compromises ?? '—'}</td><td className="px-3 py-2">{point.openBreaches ?? '—'}</td></tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_1fr]">
                            <div className="space-y-3">
                              {followup.sentEmailVariant && (
                                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Version envoyée</p>
                                  <p className="mt-1 font-semibold">{followup.sentEmailVariant.label || followup.sentEmailVariant.key}</p>
                                  {followup.sentAt && <p className="mt-0.5 text-xs text-emerald-700">Envoyée le {formatDate(followup.sentAt)}</p>}
                                  {followup.sentEmailVariant.subject && <p className="mt-2 text-xs"><span className="font-semibold">Objet :</span> {followup.sentEmailVariant.subject}</p>}
                                </div>
                              )}
                              {(followup.sentEmailHistory || []).length > 0 && (
                                <details className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
                                  <summary className="cursor-pointer font-semibold text-slate-900">Historique des envois ({followup.sentEmailHistory?.length || 0})</summary>
                                  <div className="mt-2 space-y-2">
                                    {(followup.sentEmailHistory || []).map((entry, index) => (
                                      <div key={`${entry.key || 'email'}-${index}`} className="rounded-lg bg-slate-50 p-2 text-xs">
                                        <p className="font-semibold text-slate-900">{entry.label || entry.key || 'Version envoyée'}{entry.sentAt ? ` · ${formatDate(String(entry.sentAt))}` : ''}</p>
                                        {entry.subject && <p className="mt-1 text-blue-700">Objet : {String(entry.subject)}</p>}
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Recommandations</p>
                                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
                                  {(followup.generatedSummary.recommendations || []).map((item) => <li key={item}>{item}</li>)}
                                </ul>
                              </div>
                              <label className="block text-sm font-semibold text-slate-700">
                                Satisfaction client
                                <select value={followup.clientSatisfaction || ''} onChange={(event) => void handleUpdateFollowup(followup.id, { clientSatisfaction: event.target.value ? Number(event.target.value) : null })} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:border-blue-500">
                                  <option value="">Non notée</option>
                                  <option value="5">5 — très satisfait</option>
                                  <option value="4">4 — satisfait</option>
                                  <option value="3">3 — neutre</option>
                                  <option value="2">2 — insatisfait</option>
                                  <option value="1">1 — très insatisfait</option>
                                </select>
                              </label>
                              <label className="block text-sm font-semibold text-slate-700">
                                Questions / réponse client
                                <textarea defaultValue={followup.clientQuestions || ''} onBlur={(event) => void handleUpdateFollowup(followup.id, { clientQuestions: event.target.value })} rows={3} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:border-blue-500" />
                              </label>
                              <label className="block text-sm font-semibold text-slate-700">
                                Prochaine action
                                <textarea defaultValue={followup.nextAction || ''} onBlur={(event) => void handleUpdateFollowup(followup.id, { nextAction: event.target.value })} rows={2} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:border-blue-500" />
                              </label>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Options de courriel proposées</p>
                              {(() => {
                                const variants = Object.entries(getFollowupEmailVariants(followup));
                                const selectedKey = selectedEmailVariantByFollowupId[followup.id] || variants[0]?.[0];
                                const selectedVariant = variants.find(([key]) => key === selectedKey)?.[1] || variants[0]?.[1];
                                if (!selectedVariant) return <p className="mt-3 text-sm text-slate-600">Aucune version de courriel disponible.</p>;
                                return (
                                  <div className="mt-3 space-y-3">
                                    <div className="flex flex-wrap gap-2">
                                      {variants.map(([key, variant]) => {
                                        const isSelected = selectedKey === key;
                                        return (
                                          <button
                                            key={key}
                                            type="button"
                                            onClick={() => void handleUseEmailVariant(followup.id, key)}
                                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${isSelected ? 'bg-blue-600 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                                          >
                                            {variant.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                    <div className="rounded-lg border border-blue-100 bg-white p-3">
                                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                        <div>
                                          <p className="text-sm font-semibold text-slate-900">{selectedVariant.label}</p>
                                          <p className="mt-1 text-xs font-semibold text-blue-700">Objet : {selectedVariant.subject}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          <button type="button" onClick={() => void handleCopyEmailVariant(`${selectedVariant.subject}\n\n${selectedVariant.body}`)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Copier</button>
                                          <button type="button" onClick={() => void handleMarkEmailVariantSent(followup, selectedKey, selectedVariant)} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500">Marquer envoyé avec cette version</button>
                                        </div>
                                      </div>
                                      <pre className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs text-slate-700">{selectedVariant.body}</pre>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeView === 'dashboard' && (
              <>
              <div data-usecure-dashboard-summary className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Clients suivis</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{number(dashboardClients.length || summary?.clientWithActiveCampaignsCount)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Apprenants actifs</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{number(totalLearners)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Complétion formation</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{pct(trainingCompletion)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Utilisateurs à risque</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{number(highRiskLearners)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Expositions ouvertes</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{number(openBreaches)}</p>
                </div>
              </div>

              {loading && <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">Chargement des campagnes uSecure…</div>}

              {!loading && (!dashboard || filteredClients.length === 0) && (
                <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">
                  {dashboard?.fromCache && !dashboard?.lastSyncAt
                    ? 'Aucune synchronisation uSecure n’est encore sauvegardée dans PRIsME. Clique sur Actualiser les données une première fois.'
                    : 'Aucun client uSecure synchronisé pour le moment.'}
                </div>
              )}

              {!loading && dashboard && filteredClients.length > 0 && (
                <div className="grid gap-4">
                  <div className="space-y-3">
                    {filteredClients.map((client) => (
                      <div key={client.usecureCompanyId} data-usecure-client-card className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-slate-900">{client.name}</h3>
                            <p className="text-xs text-slate-400">ID uSecure : {client.usecureCompanyId}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {client.services.map((service) => (
                              <span key={service.key} className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
                                {service.key === 'uLearn' ? <GraduationCap className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}
                                {service.label}
                              </span>
                            ))}
                            <button type="button" onClick={() => openClientFollowup(client.usecureCompanyId)} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                              Voir suivi mensuel
                            </button>
                          </div>
                        </div>

                        <div className="mb-3 grid gap-2 sm:grid-cols-3">
                          <div data-usecure-client-360 className="rounded-xl border border-blue-100 bg-blue-50 p-2.5 sm:col-span-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">Fiche client uSecure 360</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">{client.services.map((service) => service.label).join(' · ') || 'Services à valider'} · dernier suivi : {followups.find((followup) => followup.usecureCompanyId === client.usecureCompanyId)?.status || 'non généré'}</p>
                            <p className="text-xs text-slate-600">Projet PRIsME : {followups.find((followup) => followup.usecureCompanyId === client.usecureCompanyId)?.servoComparison?.projectLink?.projectTitle || 'à associer'} · prochaine action : {followups.find((followup) => followup.usecureCompanyId === client.usecureCompanyId)?.nextAction || 'générer le suivi mensuel'}</p>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Formations</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">{pct(client.courseSummary?.completionRate)} complétées</p>
                            <p className="text-xs text-slate-400">{number(client.courseSummary?.completedCount)} / {number(client.courseSummary?.assignedCount)} assignations · score moyen {number(client.courseSummary?.averageScore)}</p>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Risque humain</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">{number(client.learnerSummary?.highRiskCount)} à risque élevé</p>
                            <p className="text-xs text-slate-400">{number(client.learnerSummary?.activeCount)} actifs · {number(client.userSummary?.unverifiedCount)} admin(s) non vérifié(s)</p>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">uBreach</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">{number(client.breachSummary?.openCount)} exposition(s) ouvertes</p>
                            <p className="text-xs text-slate-400">{number(client.breachSummary?.openHighCount)} haute priorité · {(client.uBreachProDomains || []).join(', ') || 'Aucun domaine listé'}</p>
                          </div>
                        </div>

                        {client.activeCampaigns.length === 0 ? (
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                            Aucune campagne d’hameçonnage active synchronisée pour ce client. Les données de formation, d’utilisation et d’exposition restent disponibles pour le suivi mensuel.
                          </div>
                        ) : (
                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                          <table className="w-full min-w-[920px] text-left text-xs">
                            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                              <tr>
                                <th className="px-3 py-2">Campagne</th>
                                <th className="px-3 py-2">Statut</th>
                                <th className="px-3 py-2">Période</th>
                                <th className="px-3 py-2 text-right">Envoyés</th>
                                <th className="px-3 py-2 text-right">Ouverts</th>
                                <th className="px-3 py-2 text-right">Cliqués</th>
                                <th className="px-3 py-2 text-right">Compromis</th>
                                <th className="px-3 py-2 text-right">Signalés</th>
                                <th className="px-3 py-2">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {client.activeCampaigns.map((campaign) => (
                                <tr key={campaign.id} className={selectedCampaign?.id === campaign.id ? 'bg-blue-50' : ''}>
                                  <td className="px-3 py-2 text-slate-900">
                                    <p className="font-semibold">{campaign.name}</p>
                                    <p className="text-xs text-slate-400">{campaign.deliveryMethod || 'Méthode non précisée'}</p>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${campaignRiskTone(campaign)}`}>{statusLabel(campaign.status)}</span>
                                  </td>
                                  <td className="px-3 py-2 text-slate-600">{formatDate(campaign.startDate)} → {formatDate(campaign.endDate)}</td>
                                  <td className="px-3 py-2 text-right text-slate-700">{number(campaign.sentCount)}</td>
                                  <td className="px-3 py-2 text-right text-slate-700">{number(campaign.openCount)} <span className="text-xs text-slate-500">({pct(campaign.metrics?.openRate)})</span></td>
                                  <td className="px-3 py-2 text-right text-slate-700">{number(campaign.visitCount)} <span className="text-xs text-slate-500">({pct(campaign.metrics?.visitRate)})</span></td>
                                  <td className="px-3 py-2 text-right text-slate-700">{number(campaign.compromiseCount)} <span className="text-xs text-slate-500">({pct(campaign.metrics?.compromiseRate)})</span></td>
                                  <td className="px-3 py-2 text-right text-slate-700">{number(campaign.reportCount)} <span className="text-xs text-slate-500">({pct(campaign.metrics?.reportRate)})</span></td>
                                  <td className="px-3 py-2">
                                    <button onClick={() => selectCampaign(campaign)} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100">
                                      <Eye className="h-4 w-4" />
                                      Voir
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {emailDetailOpen && selectedCampaign && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm" onClick={() => setEmailDetailOpen(false)}>
                      <aside className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl shadow-slate-900/25" onClick={(event) => event.stopPropagation()}>
                        <div className="mb-5 flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-bold text-slate-900">Détail du courriel</h3>
                            <p className="mt-1 text-sm text-slate-600">Adresse d’envoi, liens et visuel rattachés à la campagne sélectionnée.</p>
                          </div>
                          <button type="button" onClick={() => setEmailDetailOpen(false)} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900" aria-label="Fermer le détail du courriel">
                            <X className="h-5 w-5" />
                          </button>
                        </div>

                        <div className="space-y-4">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs uppercase tracking-wide text-slate-400">Campagne</p>
                          <p className="mt-1 font-semibold text-slate-900">{selectedCampaign.name}</p>
                        </div>

                        <label className="block text-sm font-semibold text-slate-700">
                          Nom d’expéditeur
                          <input value={draft.fromName} onChange={(event) => setDraft({ ...draft, fromName: event.target.value })} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:border-blue-500" placeholder="Ex. Microsoft 365" />
                        </label>

                        <label className="block text-sm font-semibold text-slate-700">
                          Adresse d’envoi
                          <div className="relative mt-2">
                            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                            <input value={draft.fromAddress} onChange={(event) => setDraft({ ...draft, fromAddress: event.target.value })} className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-slate-900 outline-none focus:border-blue-500" placeholder="exemple@domaine.test" />
                          </div>
                        </label>

                        <label className="block text-sm font-semibold text-slate-700">
                          Sujet du courriel
                          <input value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value })} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:border-blue-500" placeholder="Sujet utilisé dans la campagne" />
                        </label>

                        <label className="block text-sm font-semibold text-slate-700">
                          Liens utilisés
                          <div className="relative mt-2">
                            <LinkIcon className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                            <textarea value={draft.linksText} onChange={(event) => setDraft({ ...draft, linksText: event.target.value })} rows={3} className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-slate-900 outline-none focus:border-blue-500" placeholder="Un lien par ligne" />
                          </div>
                        </label>

                        <label className="block text-sm font-semibold text-slate-700">
                          URL d’image / capture du courriel
                          <input value={draft.previewImageUrl} onChange={(event) => setDraft({ ...draft, previewImageUrl: event.target.value })} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:border-blue-500" placeholder="https://…" />
                        </label>

                        <label className="block text-sm font-semibold text-slate-700">
                          HTML du courriel, si disponible
                          <textarea value={draft.previewHtml} onChange={(event) => setDraft({ ...draft, previewHtml: event.target.value })} rows={4} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-900 outline-none focus:border-blue-500" placeholder="Coller le HTML du courriel ici…" />
                        </label>

                        <button onClick={handleSaveAsset} disabled={savingAsset} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
                          <Save className="h-4 w-4" />
                          Sauvegarder dans PRIsME
                        </button>

                        {assetMessage && <p className="rounded-xl bg-blue-50 p-3 text-sm text-blue-800">{assetMessage}</p>}

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <p className="mb-3 text-sm font-semibold text-slate-900">Aperçu</p>
                          {draft.previewImageUrl ? (
                            <img src={draft.previewImageUrl} alt="Aperçu du courriel" className="max-h-80 w-full rounded-lg object-contain" />
                          ) : draft.previewHtml ? (
                            <iframe title="Aperçu du courriel" sandbox="" srcDoc={draft.previewHtml} className="h-80 w-full rounded-lg bg-white" />
                          ) : (
                            <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                              Aucun visuel disponible. Ajoute une capture ou colle le HTML du courriel.
                            </div>
                          )}
                        </div>
                      </div>
                      </aside>
                    </div>
                  )}
                </div>
              )}
              </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
